import { ChangeDetectorRef, Component, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { take, filter, switchMap, catchError } from 'rxjs/operators';
import { of, forkJoin } from 'rxjs';
import { PaypointService } from '../../services/paypoint.service';
import { PaypointResponse } from '../../models/paypoint-response.interface';

interface RoutingRule {
  label: string;
  priority: number;
  target: { provider: string; code: string };
}

interface ValidationMessage {
  type: 'success' | 'warning';
  message: string;
}

@Component({
  selector: 'kt-bridgepay-paypoint',
  templateUrl: './paypoint.component.html',
  styleUrls: ['./paypoint.component.scss'],
})
export class PaypointComponent implements OnInit {
  organization: string;
  officeId: string;
  enableAddPaypoint = false;
  enableAddRule = false;

  paypoints: PaypointResponse[] = [];
  routingRules: RoutingRule[] = [];
  validationMessages: ValidationMessage[] = [];

  constructor(
    private route: ActivatedRoute,
    private paypointService: PaypointService,
    private cd: ChangeDetectorRef,
  ) {}

  ngOnInit() {
    this.route.params
      .pipe(
        take(1),
        filter((params) => params['companyUUID'] && params['officeUUID']),
        switchMap((params) => {
          this.organization = params['companyUUID'];
          this.officeId = params['officeUUID'];

          // Load both paypoints and routing rules
          return forkJoin({
            paypoints: this.paypointService.getOfficePaypoints(this.organization, this.officeId),
            routingRules: this.paypointService
              .getRoutingRule(this.organization, this.officeId)
              .pipe(
                // Handle error if no routing rules exist yet
                catchError(() => of({ data: [] })),
              ),
          });
        }),
      )
      .subscribe(({ paypoints, routingRules }) => {
        this.paypoints = paypoints.data || [];
        // Transform routing rules data if needed
        if (routingRules.data) {
          this.routingRules = this.transformRoutingRules(routingRules.data);
        }
        this.cd.detectChanges();
      });
  }

  onAddPaypoint() {
    this.enableAddPaypoint = true;
  }

  onPaypointAdded() {
    this.enableAddPaypoint = false;
    this.refreshPaypoints();
  }

  private refreshPaypoints() {
    this.paypointService
      .getOfficePaypoints(this.organization, this.officeId)
      .subscribe((response) => {
        this.paypoints = response.data;
        this.cd.detectChanges();
      });
  }

  onAddRule() {
    this.enableAddRule = true;
  }

  onRuleAdded() {
    this.enableAddRule = false;
    this.refreshRoutingRules();
  }

  private refreshRoutingRules() {
    this.paypointService.getRoutingRule(this.organization, this.officeId).subscribe(
      (response) => {
        if (response.data) {
          this.routingRules = this.transformRoutingRules(response.data);
        }
        this.cd.detectChanges();
      },
      () => {
        this.routingRules = [];
        this.cd.detectChanges();
      },
    );
  }

  private transformRoutingRules(data: any[]): RoutingRule[] {
    // Transform the API response to match the RoutingRule interface
    // Adjust this based on your actual API response structure
    return data.map((rule, index) => ({
      label: rule.payment_method || rule.label || `Rule ${index + 1}`,
      priority: rule.priority || index,
      target: {
        provider: rule.target_paypoint?.provider || rule.provider || '',
        code: rule.target_paypoint?.code || rule.code || '',
      },
    }));
  }
}
